import { useQuery } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

interface TwilioTokenResponse {
  success: boolean;
  token?: string;
  identity?: string;
  serviceSid?: string;
  error?: string;
}

/**
 * Fetches a Twilio Conversations access token from the edge function.
 * Auto-refreshes 5 minutes before expiry (55-min staleTime on a 60-min token).
 * Only fetches when the user is authenticated.
 */
export function useTwilioConversationsToken(enabled = true) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    // Check current session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setIsAuthenticated(!!session?.user);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setIsAuthenticated(!!session?.user);
    });

    return () => subscription.unsubscribe();
  }, []);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['twilio-conversations-token'],
    queryFn: async (): Promise<TwilioTokenResponse> => {
      console.log('[TwilioToken] Fetching access token...');
      const { data, error } = await supabase.functions.invoke('twilio-conversations-token');

      if (error) {
        console.error('[TwilioToken] Error:', error);
        throw new Error('TWILIO_TOKEN_FETCH_FAILED');
      }

      const result = data as TwilioTokenResponse;
      if (!result?.success || !result.token) {
        throw new Error(result?.error || 'TWILIO_TOKEN_INVALID');
      }

      console.log('[TwilioToken] Token received for identity:', result.identity?.slice(0, 8));
      return result;
    },
    enabled: enabled && isAuthenticated,
    staleTime: 55 * 60 * 1000, // Refresh 5 min before 60-min expiry
    gcTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 2,
  });

  return {
    token: data?.token ?? null,
    identity: data?.identity ?? null,
    serviceSid: data?.serviceSid ?? null,
    isLoading: enabled && isAuthenticated ? isLoading : false,
    error: error as Error | null,
    refetch,
  };
}
